import { Fragment, useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useParams } from "react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import { SUPPORTED_SAGE_VERSIONS } from "@sage-mcp/shared";
import {
  api,
  ApiError,
  companyDisplayName,
  configCompanies,
  type AdminUser,
  type Customer,
  type CustomerUsageResponse,
  type License,
  type Machine,
  type ClientConfig,
  type Plan,
} from "../api.js";
import { useAuth, useToken } from "../auth.js";
import { Badge, licenseStatusTone, machineStatusTone } from "../components/Badge.js";
import { CopyButton } from "../components/CopyButton.js";
import { GroupButton } from "../components/GroupButton.js";
import { TransferLicenseDialog } from "../components/TransferLicenseDialog.js";
import { ChangeLicensePlanDialog } from "../components/ChangeLicensePlanDialog.js";
import { FeatureSelector } from "../components/FeatureSelector.js";
import { DEFAULT_FEATURES } from "../features-catalog.js";
import { CREDIT_REASON_LABELS } from "../credit-reason-labels.js";

// Every dollar figure on this page reads at cent precision, credited amounts
// included, so a credited column lines up with the cost column beside it.
// Deliberate trade-off: a single credited conversation is often worth less than
// a cent and shows here as $0.00 — Conversations.tsx keeps its six-decimal
// formatCost for the per-turn view where that precision is the point.
function formatUsd(n: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

// Column labels of the history chart live in a band about one line wide, so they
// use compact notation ($1.2K) instead of the cent-precise formatUsd of the
// tables. The exact figures stay in the column's tooltip.
function formatUsdCompact(n: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: n < 1000 ? 2 : 1,
  }).format(n);
}

// "2026-07" → "juil." / "Jul" in the admin's locale. No year on the axis: a
// 12-month window holds each month exactly once, so the ordering already places
// it, and the full YYYY-MM stays in the column's tooltip.
function formatHistoryMonth(month: string, locale: string): string {
  const [year, m] = month.split("-").map(Number);
  if (!year || !m) return month.slice(5);
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, m - 1, 1)));
}

// Rows per page in the credited-conversations table.
const CREDITED_PAGE_SIZE = 5;

// Pager chevrons — same inline-SVG convention as components/CopyButton.tsx:
// 16-unit viewBox, currentColor, hidden from the accessibility tree since the
// button carries the label.
const chevronLeftIcon = (
  <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
    <path
      d="M10 3 5 8l5 5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const chevronRightIcon = (
  <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
    <path
      d="M6 3l5 5-5 5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Pencil of the header fact boxes — same inline-SVG convention as the chevrons
// above: 16-unit viewBox, currentColor, hidden from the accessibility tree
// because the button carries the label.
const pencilIcon = (
  <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
    <path
      d="M11.1 2.4a1.4 1.4 0 0 1 2 2l-7.3 7.3-2.7.7.7-2.7 7.3-7.3Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M9.7 3.8l2.5 2.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
  </svg>
);

// One identity fact of the customer header: label, value, optional secondary
// line, and an optional pencil that opens the matching dialog. Without `onEdit`
// (a reseller looking at a customer-level knob they can't turn) the box renders
// read-only rather than showing a disabled control.
function FactCard({
  label,
  children,
  sub,
  onEdit,
  editLabel,
}: {
  label: string;
  children: ReactNode;
  sub?: string | null;
  onEdit?: () => void;
  editLabel?: string;
}) {
  return (
    <div className="fact-card">
      <div className="fact-head">
        <span className="fact-label">{label}</span>
        {onEdit && (
          <button
            type="button"
            className="fact-edit"
            title={editLabel}
            aria-label={editLabel}
            onClick={onEdit}
          >
            {pencilIcon}
          </button>
        )}
      </div>
      <div className="fact-value">{children}</div>
      {sub ? <div className="fact-sub" title={sub}>{sub}</div> : null}
    </div>
  );
}

// Credited amount of a history month, normalized to null when the field is
// absent (an older cloud-api) or not a finite number — the chart and the tooltip
// both treat null as "no credited series", never as zero.
function historyCredited(h: { creditedUsd: number | null }): number | null {
  const v = h.creditedUsd;
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const token = useToken();
  const { user, emergency } = useAuth();
  // Customer-level knobs (plan, Sage version, reseller reassignment) stay
  // superuser-only — resellers can't change what plan their customer is on or
  // move the customer to another partner. Remote config creation, publishing
  // and secret sealing are open to resellers for the customers they own; the
  // cloud enforces scope with `assertCustomerAccess`.
  const canEditCustomer = emergency || user?.role !== "reseller";
  const { t, i18n } = useLingui();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [licenses, setLicenses] = useState<License[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [configs, setConfigs] = useState<ClientConfig[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [newLicenseOpen, setNewLicenseOpen] = useState(false);
  const [editingLicense, setEditingLicense] = useState<License | null>(null);
  const [transferLicense, setTransferLicense] = useState<License | null>(null);
  const [transferredLicense, setTransferredLicense] = useState<License | null>(null);
  const [planLicense, setPlanLicense] = useState<License | null>(null);
  const [changingSageVersion, setChangingSageVersion] = useState(false);
  const [reassigningReseller, setReassigningReseller] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [usage, setUsage] = useState<CustomerUsageResponse | null>(null);
  // Pause/unpause is superuser-only on the cloud; hide the UI for resellers.
  const canPauseCustomer = emergency || user?.role === "superuser";

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [c, l, m, cfg, p, u] = await Promise.all([
        api.getCustomer(token, id),
        api.listLicenses(token, id),
        api.listMachines(token, id),
        api.listConfigs(token, id),
        api.listPlans(token),
        api.customerUsage(token, id).catch(() => null),
      ]);
      setCustomer(c.customer);
      setLicenses(l.licenses);
      setMachines(m.machines);
      setConfigs(cfg.configs);
      setPlans(p.plans);
      setUsage(u);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const revokeMachineAction = async (machineId: string) => {
    if (!confirm(t`Revoke this machine? The agent will be locked out at the next request.`)) return;
    setBusyAction(`revoke-${machineId}`);
    try {
      await api.revokeMachine(token, machineId);
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyAction(null);
    }
  };

  const reactivateMachineAction = async (machineId: string) => {
    if (!confirm(t`Reactivate this machine? The agent will resume at its next request.`)) return;
    setBusyAction(`reactivate-${machineId}`);
    try {
      await api.reactivateMachine(token, machineId);
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError && err.code === "E_CONFLICT"
        ? t`This license is occupied or the machine has no license. Administrator authorization is required before it can return.`
        : err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyAction(null);
    }
  };

  const suspendLicenseAction = async (l: License) => {
    if (!confirm(t`Suspend licence ${l.licenseKey}? Agents on this key will stop working immediately.`))
      return;
    setBusyAction(`suspend-${l.id}`);
    try {
      await api.suspendLicense(token, l.id);
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyAction(null);
    }
  };

  const reactivateLicenseAction = async (l: License) => {
    if (!confirm(t`Reactivate licence ${l.licenseKey}?`)) return;
    setBusyAction(`reactivate-lic-${l.id}`);
    try {
      await api.reactivateLicense(token, l.id);
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyAction(null);
    }
  };

  const deleteLicenseAction = async (l: License) => {
    if (
      !confirm(
        t`Delete licence ${l.licenseKey}? This cannot be undone. Any machine still bound to it must be deleted first.`,
      )
    )
      return;
    setBusyAction(`delete-lic-${l.id}`);
    try {
      await api.deleteLicense(token, l.id);
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyAction(null);
    }
  };

  const deleteMachineAction = async (machineId: string) => {
    if (
      !confirm(
        t`Delete this machine? This wipes its heartbeats, config state and encrypted secrets. This cannot be undone.`,
      )
    )
      return;
    setBusyAction(`delete-${machineId}`);
    try {
      await api.deleteMachine(token, machineId);
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyAction(null);
    }
  };

  const deactivateAction = async () => {
    if (!customer) return;
    const reason = prompt(
      t`Deactivate ${customer.name}? Every licence attached to this customer will stop working at the next agent request. You can add a short note (optional):`,
      "",
    );
    if (reason === null) return; // user cancelled
    const trimmed = reason.trim();
    setBusyAction("deactivate-customer");
    try {
      await api.deactivateCustomer(token, customer.id, trimmed || undefined);
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyAction(null);
    }
  };

  const reactivateCustomerAction = async () => {
    if (!customer) return;
    if (!confirm(t`Reactivate ${customer.name}? Licences resume at the next agent request.`)) return;
    setBusyAction("reactivate-customer");
    try {
      await api.reactivateCustomer(token, customer.id);
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyAction(null);
    }
  };

  const pauseAction = async () => {
    if (!customer) return;
    const reason = prompt(
      t`Pause ${customer.name} for non-payment? Access is cut until you unpause. Optional note:`,
      "",
    );
    if (reason === null) return;
    const trimmed = reason.trim();
    setBusyAction("pause-customer");
    try {
      await api.pauseCustomer(token, customer.id, trimmed || undefined);
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyAction(null);
    }
  };

  const unpauseAction = async () => {
    if (!customer) return;
    if (!confirm(t`Unpause ${customer.name}? Access resumes at the next agent request.`)) return;
    setBusyAction("unpause-customer");
    try {
      await api.unpauseCustomer(token, customer.id);
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyAction(null);
    }
  };

  // Publish is gone with migration 057: the config is one mutable row, not a
  // series of draft/publish versions. The button below now navigates straight to
  // the Edit screen instead.

  if (loading) return <p className="muted"><Trans>Loading…</Trans></p>;
  if (error) return <div className="banner err">{error}</div>;
  if (!customer) return <p className="muted"><Trans>Customer not found.</Trans></p>;

  return (
    <div className="detail">
      {/* Name on the left, identity facts filling the rest of the header line:
          KPI-style boxes (same shell as the Monitoring header) each carrying
          its own pencil, then the two lifecycle switches stacked at the far
          right. Below ~1100px the facts wrap under the name. */}
      <header className="page-header customer-header">
        <div className="customer-ident">
          <h1>{customer.name}</h1>
          <p className="muted">
            {customer.contactName ? `${customer.contactName} · ` : ""}
            {customer.contactEmail}
          </p>
        </div>

        <div className="fact-row">
          <div className="fact-cards">
            {canEditCustomer && (
              <FactCard
                label={t`Reseller`}
                onEdit={() => setReassigningReseller(true)}
                editLabel={t`Reassign reseller`}
                sub={
                  customer.resellerId && customer.resellerName
                    ? customer.resellerEmail
                    : null
                }
              >
                {customer.resellerId ? (
                  customer.resellerName ?? customer.resellerEmail
                ) : (
                  <em><Trans>Direct (Jalfa)</Trans></em>
                )}
              </FactCard>
            )}
            <FactCard
              label={t`Licence plans`}
            >
              {[...new Set(licenses.map(l => l.planName))].join(", ") || "—"}
            </FactCard>
            <FactCard
              label={t`Sage version`}
              onEdit={canEditCustomer ? () => setChangingSageVersion(true) : undefined}
              editLabel={t`Change Sage version`}
            >
              {customer.sageVersion}
            </FactCard>
          </div>
          <div className="fact-actions">
            {customer.deactivatedAt ? (
              <button
                disabled={busyAction === "reactivate-customer"}
                onClick={() => void reactivateCustomerAction()}
              >
                <Trans>Reactivate customer</Trans>
              </button>
            ) : (
              <button
                className="tinted-err"
                disabled={busyAction === "deactivate-customer"}
                onClick={() => void deactivateAction()}
              >
                <Trans>Deactivate customer</Trans>
              </button>
            )}
            {canPauseCustomer && (customer.pausedAt ? (
              <button
                disabled={busyAction === "unpause-customer"}
                onClick={() => void unpauseAction()}
              >
                <Trans>Unpause (paid)</Trans>
              </button>
            ) : (
              <button
                className="tinted-warn"
                disabled={busyAction === "pause-customer"}
                onClick={() => void pauseAction()}
              >
                <Trans>Pause (unpaid)</Trans>
              </button>
            ))}
          </div>
        </div>
      </header>

      {(customer.deactivatedAt || customer.pausedAt) && (
        <div className={`banner ${customer.deactivatedAt ? "err" : "warn"}`}>
          {customer.deactivatedAt ? (
            <>
              <strong><Trans>Customer deactivated</Trans></strong>{" "}
              <span className="muted small">
                <Trans>
                  since {new Date(customer.deactivatedAt).toLocaleString(i18n.locale)}
                </Trans>
                {customer.deactivatedReason ? ` — ${customer.deactivatedReason}` : ""}
              </span>
            </>
          ) : null}
          {customer.deactivatedAt && customer.pausedAt ? <br /> : null}
          {customer.pausedAt ? (
            <>
              <strong><Trans>Paused for non-payment</Trans></strong>{" "}
              <span className="muted small">
                <Trans>
                  since {new Date(customer.pausedAt).toLocaleString(i18n.locale)}
                </Trans>
                {customer.pausedReason ? ` — ${customer.pausedReason}` : ""}
              </span>
            </>
          ) : null}
          <div className="muted small" style={{ marginTop: 4 }}>
            <Trans>
              Every licence attached to this customer is refused at the cloud auth
              layer. Individual licence/machine statuses are unchanged.
            </Trans>
          </div>
        </div>
      )}

      {usage && <UsageSection usage={usage} />}

      <section>
        <div className="section-header">
          <h2><Trans>Licences</Trans></h2>
          <button onClick={() => setNewLicenseOpen(true)}><Trans>Issue licence</Trans></button>
        </div>
        {licenses.length === 0 ? (
          <p className="muted"><Trans>No licence issued yet.</Trans></p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th><Trans>Label</Trans></th>
                <th><Trans>Key</Trans></th>
                <th><Trans>Plan</Trans></th>
                <th><Trans>Status</Trans></th>
                <th><Trans>Current machine</Trans></th>
                <th><Trans>Monthly allowance</Trans></th>
                {usage && <th><Trans>Cost</Trans></th>}
                <th />
              </tr>
            </thead>
            <tbody>
              {licenses.map((l) => {
                const licenseUsage = usage?.byLicense.find(row => row.licenseId === l.id);
                return (
                  <Fragment key={l.id}>
                    <tr>
                      <td>{l.label ?? <span className="muted">—</span>}</td>
                      <td>
                        <span className="copy-cell">
                          <code>{l.licenseKey}</code>
                          <CopyButton value={l.licenseKey} label={t`Copy licence key`} />
                        </span>
                      </td>
                      <td>
                        {l.planName} <code>{l.planCode}</code>
                        {plans.some(p => p.id === l.planId) && (
                          <button className="link-btn" onClick={() => setPlanLicense(l)}>
                            <Trans>Change licence plan</Trans>
                          </button>
                        )}
                      </td>
                      <td>
                        <Badge tone={licenseStatusTone(l.status)}>{l.status}</Badge>
                      </td>
                      <td>{l.currentMachineName ?? l.currentMachineId ?? "—"}</td>
                      <td>
                        {`$${l.monthlyBudgetUsd.toFixed(2)}`}
                      </td>
                      {usage && <td>{licenseUsage ? formatUsd(licenseUsage.costUsd, i18n.locale) : "—"}</td>}
                      <td>
                        <GroupButton
                          label={<Trans>Edit</Trans>}
                          dropdownLabel={t`Licence actions`}
                          onClick={() => setEditingLicense(l)}
                        >
                          <button className="link-btn" onClick={() => setTransferLicense(l)}><Trans>Transfer licence</Trans></button>
                          <button
                            className="link-btn"
                            onClick={() => setEditingLicense(l)}
                          >
                            <Trans>Edit</Trans>
                          </button>
                          {l.status === "suspended" || l.status === "canceled" ? (
                            <button
                              className="link-btn"
                              disabled={busyAction === `reactivate-lic-${l.id}`}
                              onClick={() => void reactivateLicenseAction(l)}
                            >
                              <Trans>Reactivate</Trans>
                            </button>
                          ) : (
                            <button
                              className="link-btn"
                              disabled={busyAction === `suspend-${l.id}`}
                              onClick={() => void suspendLicenseAction(l)}
                            >
                              <Trans>Suspend</Trans>
                            </button>
                          )}
                          <button
                            className="link-btn danger"
                            disabled={busyAction === `delete-lic-${l.id}`}
                            onClick={() => void deleteLicenseAction(l)}
                          >
                            <Trans>Delete</Trans>
                          </button>
                        </GroupButton>
                      </td>
                    </tr>
                    {usage && (
                      <tr className="license-usage-row">
                        <td colSpan={8}>
                          <details className="license-usage-details">
                            <summary><Trans>Usage this month ({usage.month})</Trans></summary>
                            {licenseUsage ? (
                              <dl className="license-usage-stats">
                                <div><dt><Trans>Remaining budget</Trans></dt><dd>{formatUsd(licenseUsage.remainingBudgetUsd, i18n.locale)}</dd></div>
                                <div><dt><Trans>Hard cap slack</Trans></dt><dd>{formatUsd(licenseUsage.remainingHardCapUsd, i18n.locale)}</dd></div>
                                <div><dt><Trans>Tolerance</Trans></dt><dd>{licenseUsage.plan.tolerancePct}%</dd></div>
                                <div><dt><Trans>Held reservations</Trans></dt><dd>{formatUsd(licenseUsage.heldReservationsUsd, i18n.locale)}</dd></div>
                                <div><dt><Trans>Status</Trans></dt><dd>{licenseUsage.exhausted ? <Trans>Exhausted</Trans> : <Trans>Available</Trans>}</dd></div>
                                {usage.totals.creditedUsd !== null && licenseUsage.creditedUsd !== null && (
                                  <div><dt><Trans>Credited</Trans></dt><dd>{formatUsd(licenseUsage.creditedUsd, i18n.locale)}</dd></div>
                                )}
                                <div><dt><Trans>Input tokens</Trans></dt><dd>{licenseUsage.inputTokens.toLocaleString(i18n.locale)}</dd></div>
                                <div><dt><Trans>Output tokens</Trans></dt><dd>{licenseUsage.outputTokens.toLocaleString(i18n.locale)}</dd></div>
                                <div><dt><Trans>Turns</Trans></dt><dd>{licenseUsage.turnCount}</dd></div>
                              </dl>
                            ) : <p className="muted"><Trans>No licence usage.</Trans></p>}
                          </details>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h2><Trans>Machines</Trans></h2>
        {machines.length === 0 ? (
          <p className="muted"><Trans>No machine registered yet.</Trans></p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th><Trans>Name</Trans></th>
                <th><Trans>Status</Trans></th>
                <th><Trans>Configurations</Trans></th>
                <th><Trans>Config</Trans></th>
                <th><Trans>Last heartbeat</Trans></th>
                <th />
              </tr>
            </thead>
            <tbody>
              {machines.map((m) => {
                const lic = licenses.find((l) => l.id === m.licenseId) ?? null;
                return (
                <tr key={m.id}>
                  <td>
                    {m.machineName ?? "—"}
                    {m.agentVersion && <div className="muted"><Trans>agent {m.agentVersion}</Trans></div>}
                  </td>
                  <td>
                    <Badge tone={machineStatusTone(m.status)}>{m.status}</Badge>
                  </td>
                  {/* Licence name, licence key and machine fingerprint stacked —
                      three identifiers an admin reads together when matching a
                      machine to what was issued. */}
                  <td>
                    {lic ? (
                      <>
                        <div>{lic.label ?? <span className="muted"><Trans>(no label)</Trans></span>}</div>
                        <div className="muted small">
                          <code>{lic.licenseKey}</code>
                        </div>
                      </>
                    ) : (
                      <div className="muted">—</div>
                    )}
                    <div>
                      <code className="fp">{m.publicKeyFingerprint}</code>
                    </div>
                  </td>
                  <td>
                    {m.configState?.currentConfigVersion
                      ? `v${m.configState.currentConfigVersion} · ${m.configState.lastApplyStatus ?? "?"}`
                      : "—"}
                  </td>
                  <td className="muted">
                    {m.lastHeartbeatAt
                      ? new Date(m.lastHeartbeatAt).toLocaleString(i18n.locale)
                      : t`never`}
                  </td>
                  <td>
                    <GroupButton
                      label={<Trans>Seal secrets</Trans>}
                      dropdownLabel={t`Machine actions`}
                      to={`/customers/${customer.id}/machines/${m.id}/seal`}
                    >
                      <Link to={`/customers/${customer.id}/machines/${m.id}/seal`}>
                        <Trans>Seal secrets</Trans>
                      </Link>
                      {m.status === "active" && (
                        <button
                          className="link-btn"
                          disabled={busyAction === `revoke-${m.id}`}
                          onClick={() => void revokeMachineAction(m.id)}
                        >
                          <Trans>Revoke</Trans>
                        </button>
                      )}
                      {m.status === "revoked" && (
                        <button
                          className="link-btn"
                          disabled={busyAction === `reactivate-${m.id}`}
                          onClick={() => void reactivateMachineAction(m.id)}
                        >
                          <Trans>Reactivate</Trans>
                        </button>
                      )}
                      <button
                        className="link-btn danger"
                        disabled={busyAction === `delete-${m.id}`}
                        onClick={() => void deleteMachineAction(m.id)}
                      >
                        <Trans>Delete</Trans>
                      </button>
                    </GroupButton>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <div className="section-header">
          <h2><Trans>Remote config</Trans></h2>
          {(() => {
            const c = configs[0];
            const activeId = c?.payload?.sage?.activeCompanyId ?? null;
            return (
              <div className="row-actions">
                {c && (
                  <span className="muted small">
                    <Trans>
                      rev {c.version} · {new Date(c.createdAt).toLocaleString(i18n.locale)}
                      {activeId ? ` · default ${activeId}` : ""}
                    </Trans>
                  </span>
                )}
                <Link to={`/customers/${customer.id}/configs/new`}>
                  <button>
                    {configs.length === 0 ? <Trans>Set up</Trans> : <Trans>Edit</Trans>}
                  </button>
                </Link>
              </div>
            );
          })()}
        </div>
        {configs.length === 0 ? (
          <p className="muted"><Trans>No config yet.</Trans></p>
        ) : (
          (() => {
            // Single-row-per-customer since migration 057. Same shape as the
            // Machines/Licenses tables above so this section reads as one of
            // the same family, not a bespoke summary card.
            const c = configs[0];
            const companies = configCompanies(c);
            const activeId = c.payload?.sage?.activeCompanyId ?? null;
            return (
              <table className="table">
                <thead>
                  <tr>
                    <th><Trans>Company</Trans></th>
                    <th><Trans>Id</Trans></th>
                    <th><Trans>SQL Server</Trans></th>
                    <th><Trans>Sage Web API</Trans></th>
                    <th><Trans>Status</Trans></th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((co) => {
                    const sql =
                      co.sqlServer && co.database
                        ? `${co.sqlServer}/${co.database}`
                        : null;
                    const webApi =
                      co.tenant && co.company ? `${co.tenant}/${co.company}` : null;
                    return (
                      <tr key={co.id}>
                        <td>
                          {companyDisplayName(co)}
                          {activeId === co.id && !co.disabled && (
                            <div className="muted small"><Trans>default</Trans></div>
                          )}
                        </td>
                        <td>
                          <code>{co.id}</code>
                        </td>
                        <td className="muted">
                          {sql ?? "—"}
                        </td>
                        <td className="muted">
                          {webApi ?? "—"}
                        </td>
                        <td>
                          {co.disabled ? (
                            <Badge tone="warn"><Trans>disabled</Trans></Badge>
                          ) : (
                            <Badge tone="ok"><Trans>active</Trans></Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          })()
        )}
      </section>

      {transferLicense && <TransferLicenseDialog transferVersion={transferLicense.transferVersion}
        onCancel={() => { setTransferLicense(null); void refresh(); }}
        onConfirm={async input => {
          const result = await api.transferLicense(token, transferLicense.id, input);
          setTransferredLicense(result.license);
          setTransferLicense(null);
          await refresh();
        }} />}
      {transferredLicense && <div className="modal-backdrop">
        <div className="modal" role="dialog" aria-modal="true">
          <h2><Trans>Replacement authorized</Trans></h2>
          <p><Trans>Deliver this rotated key securely to the replacement operator. Use Change license key on the desktop to validate and protect the key, then configure Sage access. Old-machine files remain on the old machine.</Trans></p>
          <code>{transferredLicense.licenseKey}</code>
          <CopyButton value={transferredLicense.licenseKey} label={t`Copy licence key`} />
          <button onClick={() => setTransferredLicense(null)}><Trans>Close</Trans></button>
        </div>
      </div>}
      {newLicenseOpen && (
        <NewLicenseDialog
          customerId={customer.id}
          plans={plans.filter((p) => p.isActive)}
          onCancel={() => setNewLicenseOpen(false)}
          onCreated={() => {
            setNewLicenseOpen(false);
            void refresh();
          }}
        />
      )}

      {editingLicense && (
        <EditLicenseDialog
          license={editingLicense}
          onCancel={() => setEditingLicense(null)}
          onSaved={() => {
            setEditingLicense(null);
            void refresh();
          }}
        />
      )}

      {planLicense && plans.find(p => p.id === planLicense.planId) && (
        <ChangeLicensePlanDialog
          currentPlan={plans.find(p => p.id === planLicense.planId)!}
          plans={plans.filter(p => p.isActive)}
          onCancel={() => setPlanLicense(null)}
          onConfirm={async planId => {
            const current = plans.find(p => p.id === planLicense.planId)!;
            const proposed = plans.find(p => p.id === planId)!;
            await api.changeLicensePlan(token, planLicense.id, {
              planId,
              ...(proposed.retentionDays < current.retentionDays ? {
                retentionConfirmation: {
                  currentPlanId: current.id,
                  currentRetentionDays: current.retentionDays,
                  proposedRetentionDays: proposed.retentionDays,
                },
              } : {}),
            });
            setPlanLicense(null);
            await refresh();
          }}
        />
      )}

      {changingSageVersion && (
        <ChangeSageVersionDialog
          customer={customer}
          onCancel={() => setChangingSageVersion(false)}
          onSaved={() => {
            setChangingSageVersion(false);
            void refresh();
          }}
        />
      )}

      {reassigningReseller && (
        <ReassignResellerDialog
          customer={customer}
          onCancel={() => setReassigningReseller(false)}
          onSaved={() => {
            setReassigningReseller(false);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

// Superuser-only: reassign a customer to a different reseller (or make it
// direct Jalfa). Loads the current reseller list on open so a newly created
// account shows up without a full page refresh.
function ReassignResellerDialog({
  customer,
  onCancel,
  onSaved,
}: {
  customer: Customer;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const token = useToken();
  // "" here means "Direct (Jalfa)" — the server accepts null for the same intent.
  const [resellerId, setResellerId] = useState<string>(customer.resellerId ?? "");
  const [resellers, setResellers] = useState<AdminUser[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .listUsers(token)
      .then((res) => {
        const active = res.users.filter(
          (u) => u.role === "reseller" && u.disabledAt === null,
        );
        setResellers(active);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.reassignCustomer(token, customer.id, resellerId === "" ? null : resellerId);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2><Trans>Reassign reseller</Trans></h2>
        <p className="muted small">
          <Trans>
            The customer keeps its licences, machines and history — only the ownership
            changes. The new reseller sees this customer on their next dashboard load.
          </Trans>
        </p>
        <form onSubmit={submit}>
          <label>
            <Trans>Reseller</Trans>
            {loading ? (
              <p className="muted small"><Trans>Loading…</Trans></p>
            ) : (
              <select value={resellerId} onChange={(e) => setResellerId(e.target.value)}>
                <option value="">— Direct (Jalfa) —</option>
                {resellers?.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name ? `${r.name} · ${r.email}` : r.email}
                  </option>
                ))}
              </select>
            )}
          </label>
          {error && <div className="banner err">{error}</div>}
          <div className="actions">
            <button type="button" className="ghost" onClick={onCancel}>
              <Trans>Cancel</Trans>
            </button>
            <button type="submit" disabled={submitting || loading}>
              {submitting ? <Trans>Saving…</Trans> : <Trans>Reassign</Trans>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ChangeSageVersionDialog({
  customer,
  onCancel,
  onSaved,
}: {
  customer: Customer;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const token = useToken();
  const [sageVersion, setSageVersion] = useState(customer.sageVersion);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // t not needed — only Trans used below

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.updateCustomer(token, customer.id, { sageVersion });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2><Trans>Change Sage version</Trans></h2>
        <p className="muted small">
          <Trans>
            Applies at the next agent config poll — the mcp-server restarts with the new
            schema bundle.
          </Trans>
        </p>
        <form onSubmit={submit}>
          <label>
            <Trans>Sage version</Trans>
            <select
              value={sageVersion}
              onChange={(e) => setSageVersion(e.target.value)}
              required
            >
              {SUPPORTED_SAGE_VERSIONS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          {error && <div className="banner err">{error}</div>}
          <div className="actions">
            <button type="button" className="ghost" onClick={onCancel}>
              <Trans>Cancel</Trans>
            </button>
            <button type="submit" disabled={submitting}>
              {submitting ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NewLicenseDialog({
  customerId,
  plans,
  onCancel,
  onCreated,
}: {
  customerId: string;
  plans: Plan[];
  onCancel: () => void;
  onCreated: () => void;
}) {
  const token = useToken();
  const { t } = useLingui();
  const [label, setLabel] = useState("");
  const [planId, setPlanId] = useState("");
  const [features, setFeatures] = useState<Set<string>>(() => new Set(DEFAULT_FEATURES));
  const [writeMode, setWriteMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.createLicense(token, {
        customerId,
        planId,
        label: label.trim() || undefined,
        features: [...features],
        writeMode,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2><Trans>Issue licence</Trans></h2>
        <p className="muted small">
          <Trans>
            Select this licence's plan before installation. Its plan provides an independent monthly allowance.
          </Trans>
        </p>
        <form onSubmit={submit}>
          <label>
            <Trans>Plan</Trans>
            <select value={planId} onChange={(e) => setPlanId(e.target.value)} required>
              <option value="" disabled><Trans>Select a plan</Trans></option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
              ))}
            </select>
          </label>
          <label>
            <Trans>Label (optional)</Trans>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t`e.g. HQ production, staging, trial 2026-Q1`}
            />
          </label>
          <FeatureSelector features={features} onChange={setFeatures} />
          <label className="inline write-mode">
            <input
              type="checkbox"
              checked={writeMode}
              onChange={(e) => setWriteMode(e.target.checked)}
            />{" "}
            <Trans>Allow write mode</Trans> <span className="muted"><Trans>(gate for every <code>.write</code>{" "}
            and <code>.post</code> feature — required in addition to the checkboxes above)</Trans></span>
          </label>
          {error && <div className="banner err">{error}</div>}
          <div className="actions">
            <button type="button" className="ghost" onClick={onCancel}>
              <Trans>Cancel</Trans>
            </button>
            <button type="submit" disabled={submitting}>
              {submitting ? <Trans>Creating…</Trans> : <Trans>Issue</Trans>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditLicenseDialog({
  license,
  onCancel,
  onSaved,
}: {
  license: License;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const token = useToken();
  const { t } = useLingui();
  const [label, setLabel] = useState(license.label ?? "");
  const [features, setFeatures] = useState<Set<string>>(() => new Set(license.features));
  const [writeMode, setWriteMode] = useState(license.writeMode);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const trimmed = label.trim();
      await api.updateLicense(token, license.id, {
        label: trimmed.length === 0 ? null : trimmed,
        features: [...features],
        writeMode,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2><Trans>Edit licence</Trans></h2>
        <p className="muted small">
          <Trans>
            Key <code>{license.licenseKey}</code> — a transfer rotates this key. This licence's plan
            is <code>{license.planCode}</code>.
          </Trans>
        </p>
        <form onSubmit={submit}>
          <label>
            <Trans>Label</Trans>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t`e.g. HQ production, staging, trial 2026-Q1`}
            />
          </label>
          <FeatureSelector features={features} onChange={setFeatures} />
          <label className="inline write-mode">
            <input
              type="checkbox"
              checked={writeMode}
              onChange={(e) => setWriteMode(e.target.checked)}
            />{" "}
            <Trans>Allow write mode</Trans> <span className="muted"><Trans>(gate for every <code>.write</code>{" "}
            and <code>.post</code> feature — required in addition to the checkboxes above)</Trans></span>
          </label>
          {error && <div className="banner err">{error}</div>}
          <div className="actions">
            <button type="button" className="ghost" onClick={onCancel}>
              <Trans>Cancel</Trans>
            </button>
            <button type="submit" disabled={submitting}>
              {submitting ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Customer usage totals, credits, 12-month history and held reservations.
function UsageSection({ usage }: { usage: CustomerUsageResponse }) {
  const { t, i18n } = useLingui();
  // A credit is an internal Jalfa arbitration: the cloud returns these fields as
  // null to a reseller, who sees only the net cost.
  const showCredits = usage.totals.creditedUsd !== null;
  // Credited conversations are paged client-side: the month's list arrives whole
  // in the payload, so there is nothing to re-fetch when the page changes.
  const [creditedPage, setCreditedPage] = useState(0);
  const creditedPageCount = Math.ceil(
    usage.creditedConversations.length / CREDITED_PAGE_SIZE,
  );
  // Clamp rather than reset: switching month can shrink the list under the
  // current page, which would otherwise render an empty table body.
  const creditedPageSafe = Math.min(creditedPage, Math.max(0, creditedPageCount - 1));
  const creditedPageRows = usage.creditedConversations.slice(
    creditedPageSafe * CREDITED_PAGE_SIZE,
    creditedPageSafe * CREDITED_PAGE_SIZE + CREDITED_PAGE_SIZE,
  );
  // Both series share one scale — the tallest single bar of either — so the two
  // columns of a month are comparable and the taller one fills the track.
  const historyMax = Math.max(
    0.01,
    ...usage.history.map((h) => Math.max(h.costUsd, historyCredited(h) ?? 0)),
  );
  return (
    <section>
      <div className="section-header">
        <h2><Trans>Usage this month ({usage.month})</Trans></h2>
      </div>
      <div className="usage-grid">
        <div className="usage-progress-card">
          <p><Trans>Total cost</Trans>: ${usage.totals.costUsd.toFixed(2)}</p>
          <p><Trans>Summed licence allowances</Trans>: ${usage.allowanceTotals.monthlyBudgetUsd.toFixed(2)}</p>
          <p><Trans>Summed licence hard caps</Trans>: ${usage.allowanceTotals.hardCapUsd.toFixed(2)}</p>
          <p className="muted"><Trans>Allowances belong to each licence and cannot be shared.</Trans></p>
        </div>
        <div className="usage-metrics">
          <div>
            <div className="kpi-label"><Trans>Input tokens</Trans></div>
            <div className="kpi-value">{usage.totals.inputTokens.toLocaleString()}</div>
          </div>
          <div>
            <div className="kpi-label"><Trans>Output tokens</Trans></div>
            <div className="kpi-value">{usage.totals.outputTokens.toLocaleString()}</div>
          </div>
          <div>
            <div className="kpi-label"><Trans>Turns</Trans></div>
            <div className="kpi-value">{usage.totals.turnCount}</div>
          </div>
          <div>
            <div className="kpi-label"><Trans>Held reservations</Trans></div>
            <div className="kpi-value">
              ${usage.totals.heldReservationsUsd.toFixed(2)}
            </div>
          </div>
          {/* creditedUsd is null for a reseller — the cloud only fills it for
              superusers, so the tile simply doesn't render for them. */}
          {usage.totals.creditedUsd !== null && (
            <div>
              <div className="kpi-label"><Trans>Credited this month</Trans></div>
              <div className="kpi-value">
                {formatUsd(usage.totals.creditedUsd, i18n.locale)}
              </div>
            </div>
          )}
        </div>
      </div>

      {showCredits && usage.creditedConversations.length > 0 && (
        <div className="credited-conversations">
          <h3><Trans>Credited conversations</Trans></h3>
          <p className="muted small">
            <Trans>
              These conversations are billed to nobody and are hidden from the
              customer's app. Together they account for the credited total above.
            </Trans>
          </p>
          <table className="table">
            <thead>
              <tr>
                <th><Trans>Conversation</Trans></th>
                <th><Trans>Reason</Trans></th>
                <th><Trans>Credited by</Trans></th>
                <th><Trans>Credited</Trans></th>
                <th style={{ textAlign: "right" }}><Trans>Amount</Trans></th>
              </tr>
            </thead>
            <tbody>
              {creditedPageRows.map((c) => (
                <tr key={c.conversationId}>
                  <td>
                    <Link to={`/conversations/${c.conversationId}`}>
                      {c.title || c.conversationId.slice(0, 8) + "…"}
                    </Link>
                    {c.note && <div className="muted small">{c.note}</div>}
                  </td>
                  <td>{i18n._(CREDIT_REASON_LABELS[c.reason])}</td>
                  <td className="muted">{c.creditedByEmail ?? "—"}</td>
                  <td className="mono">
                    {new Date(c.creditedAt).toLocaleDateString(i18n.locale)}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {formatUsd(c.costUsd, i18n.locale)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {creditedPageCount > 1 && (
            <div className="table-pager">
              <span className="muted small">
                <Trans>
                  Page {creditedPageSafe + 1} of {creditedPageCount}
                </Trans>
              </span>
              <button
                className="ghost icon"
                aria-label={t`Previous page`}
                title={t`Previous page`}
                disabled={creditedPageSafe === 0}
                onClick={() => setCreditedPage(creditedPageSafe - 1)}
              >
                {chevronLeftIcon}
              </button>
              <button
                className="ghost icon"
                aria-label={t`Next page`}
                title={t`Next page`}
                disabled={creditedPageSafe >= creditedPageCount - 1}
                onClick={() => setCreditedPage(creditedPageSafe + 1)}
              >
                {chevronRightIcon}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="history-bars">
        <h3><Trans>12-month history</Trans></h3>
        {/* Gated on the history itself, not on showCredits: the legend must not
            promise a series the payload doesn't carry. */}
        {usage.history.some((h) => historyCredited(h) !== null) && (
          <div className="bars-legend">
            <span className="bars-legend-item">
              <span className="bars-swatch billed" />
              <Trans>Billed</Trans>
            </span>
            <span className="bars-legend-item">
              <span className="bars-swatch credited" />
              <Trans>Credited</Trans>
            </span>
          </div>
        )}
        <div className="bars">
          {usage.history.map((h) => {
            // Normalized, not compared to null directly: an older cloud-api
            // omits the field entirely, and `undefined !== null` would send
            // undefined into toFixed and render "NaN".
            const credited = historyCredited(h);
            return (
              <div
                key={h.month}
                className="bar-col"
                title={
                  credited !== null
                    ? t`${h.month} — $${h.costUsd.toFixed(2)} billed · $${credited.toFixed(2)} credited · ${h.turnCount} turns`
                    : t`${h.month} — $${h.costUsd.toFixed(2)} · ${h.turnCount} turns`
                }
              >
                {/* Two bars side by side rather than stacked: billed and
                    credited are read against each other, not summed. A month
                    with no credited series renders the billed bar alone, which
                    then takes the whole group width. Each bar carries its value
                    in the band reserved above it — a zero draws the 2px stub
                    with no label rather than a row of "$0.00". */}
                <div className="bar-group">
                  <div className="bar-slot">
                    <div className="bar-value">
                      {h.costUsd > 0 && (
                        <span>{formatUsdCompact(h.costUsd, i18n.locale)}</span>
                      )}
                    </div>
                    <div className="bar-track">
                      <div
                        className="bar-fill billed"
                        style={{ height: `${(h.costUsd / historyMax) * 100}%` }}
                      />
                    </div>
                  </div>
                  {credited !== null && (
                    <div className="bar-slot">
                      <div className="bar-value">
                        {credited > 0 && (
                          <span className="credited">
                            {formatUsdCompact(credited, i18n.locale)}
                          </span>
                        )}
                      </div>
                      <div className="bar-track">
                        <div
                          className="bar-fill credited"
                          style={{ height: `${(credited / historyMax) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="bar-label">
                  {formatHistoryMonth(h.month, i18n.locale)}
                </div>
              </div>
            );
          })}
          {usage.history.length === 0 && (
            <p className="muted"><Trans>No prior months on record.</Trans></p>
          )}
        </div>
      </div>

      {usage.heldReservations.length > 0 && (
        <div className="held-reservations">
          <h3><Trans>Held reservations (in flight)</Trans></h3>
          <table className="table">
            <thead>
              <tr>
                <th><Trans>Reservation</Trans></th>
                <th><Trans>Turn</Trans></th>
                <th style={{ textAlign: "right" }}><Trans>Estimated cost</Trans></th>
                <th><Trans>Created</Trans></th>
              </tr>
            </thead>
            <tbody>
              {usage.heldReservations.map((r) => (
                <tr key={r.id}>
                  <td>
                    <code>{r.id.slice(0, 8)}…</code>
                  </td>
                  <td>
                    {r.turnId ? <code>{r.turnId.slice(0, 8)}…</code> : "—"}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    ${r.estimatedCostUsd.toFixed(4)}
                  </td>
                  <td>
                    {new Date(r.createdAt).toISOString().replace("T", " ").slice(0, 19)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
